import React from 'react';
import {
    FaIdCard, FaLocationDot, FaFileContract, FaRegCalendar, FaBuildingColumns, FaBox,
    FaTriangleExclamation, FaCircleCheck, FaCircleExclamation,
} from 'react-icons/fa6';

/**
 * Componente que muestra el encabezado del panel de detalle de cobranza,
 * incluyendo información del perfil, detalles del crédito y estado de pagos.
 * Diseño del mockup "ClientAndCreditSummary", con modo claro y oscuro.
 *
 * @param {Object} props
 * @param {Object} props.data - Datos generales del crédito
 * @param {Object} props.item - Datos del cliente
 * @param {Object} props.direccionData - Datos de dirección
 * @param {Object} props.amortizacionStats - Estadísticas de amortización calculadas
 */
export const DashboardHeader = ({ data, item, direccionData, amortizacionStats, articulosFactura = [] }) => {
    // Derived values for the premium dashboard header
    const proyectado = data?.Valor_Cobrar_Proyectado || 0;

    // Si Valor_Cobrado_Total existe y es mayor que 0 lo usamos, sino usamos el abono de amortizacion, y por ultimo Valor_Cobrado
    let cobrado = data?.Valor_Cobrado_Total > 0
        ? data.Valor_Cobrado_Total
        : (amortizacionStats?.fetched && amortizacionStats?.totalAbono > 0
            ? amortizacionStats.totalAbono
            : (data?.Valor_Cobrado || 0));

    const avance = proyectado > 0 ? Math.round((cobrado / proyectado) * 100) : 0;

    const cuotasPagadas = amortizacionStats?.fetched ? amortizacionStats.pagadas : (data?.CuotasPagadas || 0);
    const cuotasImpagas = amortizacionStats?.fetched ? amortizacionStats.impagas : (data?.CuotasVencidas || 0);
    const cuotasPendientes = amortizacionStats?.fetched ? amortizacionStats.pendientes : (data?.CuotasPendientes || 0);
    const totalCuotas = cuotasPagadas + cuotasImpagas + cuotasPendientes;
    const plazo = amortizacionStats?.fetched ? amortizacionStats.plazo : (data?.Plazo || totalCuotas || 'N/D');
    const pct = (n) => (totalCuotas > 0 ? `${(n / totalCuotas) * 100}%` : '0%');

    const diasMora = amortizacionStats?.fetched && amortizacionStats?.diasMora !== undefined
        ? amortizacionStats.diasMora
        : (data?.Dias_Mora_Proyectado || 0);
    const valorPagar = amortizacionStats?.fetched
        ? amortizacionStats.saldoImpagas.toFixed(2)
        : (data?.Cuota ?? proyectado.toFixed(2));

    const cliente = data?.Cliente || item?.Cliente || 'CLIENTE NO REGISTRADO';
    const direccion = direccionData?.canton || direccionData?.CallePrincipal
        ? `${direccionData?.canton || ''}${direccionData?.canton && direccionData?.CallePrincipal ? ', ' : ''}${direccionData?.CallePrincipal || ''}`
        : 'SIN DIRECCIÓN';

    const tarjeta = 'bg-white dark:bg-[#111827] rounded-xl border border-slate-200 dark:border-slate-800 shadow-sm p-4 flex flex-col justify-between';
    const etiqueta = 'text-[11px] text-slate-500 dark:text-slate-400 uppercase font-medium block';

    return (
        <section className="px-4 sm:px-6 pt-4 grid grid-cols-1 lg:grid-cols-12 gap-4">
            {/* Card 1: Cliente y avance de cobro */}
            <article className={`lg:col-span-4 ${tarjeta}`}>
                <div>
                    <div className="flex items-center gap-3">
                        <div className="w-12 h-12 rounded-xl bg-indigo-50 border border-indigo-200 text-indigo-600 dark:bg-indigo-950/70 dark:border-indigo-700/40 dark:text-indigo-300 flex items-center justify-center font-bold text-lg shadow-sm shrink-0">
                            {cliente.charAt(0)}
                        </div>
                        <div className="min-w-0">
                            <h2 className="text-sm font-bold text-slate-900 dark:text-white leading-tight uppercase line-clamp-1" title={cliente}>{cliente}</h2>
                            <div className="flex items-center gap-2 mt-1 text-xs text-slate-500 dark:text-slate-400">
                                <span className="inline-flex items-center gap-1 shrink-0">
                                    <FaIdCard className="text-slate-400 dark:text-slate-500" />
                                    {data?.Cedula || item?.Cedula || 'SIN CÉDULA'}
                                </span>
                                <span className="text-slate-300 dark:text-slate-600">•</span>
                                <span className="inline-flex items-center gap-1 truncate uppercase" title={direccion}>
                                    <FaLocationDot className="text-slate-400 dark:text-slate-500 shrink-0" />
                                    <span className="truncate">{direccion}</span>
                                </span>
                            </div>
                        </div>
                    </div>

                    <div className="mt-4 pt-3 border-t border-slate-200 dark:border-slate-800">
                        <div className="flex justify-between items-center text-xs mb-1.5">
                            <span className="font-medium text-slate-600 dark:text-slate-300">Avance de cobro</span>
                            <span className="font-bold text-emerald-600 dark:text-emerald-400 text-xs">{avance}% de lo proyectado</span>
                        </div>
                        <div className="w-full bg-slate-200 dark:bg-slate-800/90 rounded-full h-2.5 overflow-hidden">
                            <div className="bg-gradient-to-r from-indigo-500 to-emerald-500 h-2.5 rounded-full" style={{ width: `${Math.min(avance, 100)}%` }} />
                        </div>
                    </div>
                </div>
                <div className="mt-3 flex items-center justify-between text-xs bg-slate-50 dark:bg-[#161F30] px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-800">
                    <div>
                        <span className={etiqueta}>Proyectado</span>
                        <span className="font-semibold text-slate-800 dark:text-slate-200 text-xs">${proyectado.toFixed(2)}</span>
                    </div>
                    <div className="text-right">
                        <span className={etiqueta}>Cobrado total</span>
                        <span className="font-bold text-emerald-600 dark:text-emerald-400 text-xs">${Number(cobrado).toFixed(2)}</span>
                    </div>
                </div>
            </article>

            {/* Card 2: Detalles del crédito y productos */}
            <article className={`lg:col-span-4 ${tarjeta}`}>
                <div>
                    <div className="flex items-center justify-between pb-2 border-b border-slate-200 dark:border-slate-800">
                        <div className="flex items-center gap-2">
                            <div className="w-7 h-7 rounded-lg bg-blue-50 border border-blue-200 text-blue-600 dark:bg-blue-950/70 dark:border-blue-800/40 dark:text-blue-400 flex items-center justify-center text-xs">
                                <FaFileContract />
                            </div>
                            <h3 className="text-xs font-bold uppercase tracking-wider text-slate-700 dark:text-slate-300">Detalles del crédito</h3>
                        </div>
                        <span className="text-xs font-mono font-semibold text-indigo-700 bg-indigo-50 border-indigo-200 dark:text-indigo-300 dark:bg-indigo-950/80 dark:border-indigo-700/50 px-2 py-0.5 rounded border">
                            {data?.Numero_Documento || 'N/D'}
                        </span>
                    </div>
                    <div className="grid grid-cols-2 gap-3 mt-3">
                        <div>
                            <span className={etiqueta}>Fecha concesión</span>
                            <span className="text-xs font-medium text-slate-700 dark:text-slate-300 flex items-center gap-1.5 mt-0.5">
                                <FaRegCalendar className="text-slate-400 dark:text-slate-500 text-[11px]" />
                                {data?.Fecha_Factura ? new Date(data.Fecha_Factura).toLocaleDateString('es-EC', { day: '2-digit', month: '2-digit', year: 'numeric' }) : 'N/D'}
                            </span>
                        </div>
                        <div>
                            <span className={etiqueta}>Banco / Agencia</span>
                            <span className="text-xs font-semibold text-slate-800 dark:text-slate-200 flex items-center gap-1.5 mt-0.5 uppercase">
                                <FaBuildingColumns className="text-slate-400 dark:text-slate-500 text-[11px] shrink-0" />
                                <span className="line-clamp-2">{data?.Banco || 'N/D'}{data?.Almacen ? ` - ${data.Almacen}` : ''}</span>
                            </span>
                        </div>
                    </div>
                </div>
                <div className="mt-3">
                    <span className={`${etiqueta} mb-1`}>Productos asociados</span>
                    <div className="flex flex-wrap gap-1.5">
                        {articulosFactura.length > 0 ? (
                            <>
                                {articulosFactura.slice(0, 2).map((art, i) => (
                                    <span key={i} title={art.Articulo}
                                        className="inline-flex items-center gap-1 px-2.5 py-1 rounded bg-slate-50 border border-slate-200 text-slate-700 dark:bg-[#161F30] dark:border-slate-800 dark:hover:border-slate-700 dark:text-slate-300 text-[11px] font-medium transition cursor-default max-w-[200px]">
                                        <FaBox className="text-slate-400 dark:text-slate-500 text-[10px] shrink-0" />
                                        <span className="truncate">{art.Articulo}</span>
                                    </span>
                                ))}
                                {articulosFactura.length > 2 && (
                                    <span className="text-[10px] text-slate-400 font-medium self-center">+{articulosFactura.length - 2} más</span>
                                )}
                            </>
                        ) : (
                            <span className="text-xs font-semibold text-slate-700 dark:text-slate-300 uppercase">{data?.Cartera || 'N/D'}</span>
                        )}
                    </div>
                </div>
            </article>

            {/* Card 3: Estado de pagos */}
            <article className={`lg:col-span-4 ${tarjeta}`}>
                <div>
                    <div className="flex items-center justify-between pb-2 border-b border-slate-200 dark:border-slate-800">
                        <div className="flex items-center gap-2">
                            <div className="w-7 h-7 rounded-lg bg-rose-50 border border-rose-200 text-rose-500 dark:bg-rose-950/60 dark:border-rose-800/40 dark:text-rose-400 flex items-center justify-center text-xs">
                                <FaTriangleExclamation />
                            </div>
                            <h3 className="text-xs font-bold uppercase tracking-wider text-slate-700 dark:text-slate-300">Estado de pagos</h3>
                        </div>
                        <span className={`inline-flex items-center px-2 py-0.5 rounded text-[11px] font-bold border ${Number(diasMora) > 0
                            ? 'bg-rose-50 text-rose-600 border-rose-200 dark:bg-rose-950/80 dark:text-rose-400 dark:border-rose-800/60'
                            : 'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-950/60 dark:text-emerald-300 dark:border-emerald-800/60'}`}>
                            {Number(diasMora) > 0 ? `${diasMora} días de mora` : 'Al día'}
                        </span>
                    </div>
                    <div className="flex items-baseline justify-between mt-3">
                        <div>
                            <span className="text-[11px] font-semibold text-rose-500 dark:text-rose-400 uppercase tracking-wide block">Valor exigible / vencido</span>
                            <div className="text-2xl font-extrabold text-rose-600 dark:text-rose-400 tracking-tight">${valorPagar}</div>
                        </div>
                        <div className="text-right">
                            <span className={etiqueta}>Plazo del contrato</span>
                            <span className="text-xs font-semibold text-slate-800 dark:text-slate-200">{plazo} cuotas totales</span>
                        </div>
                    </div>
                </div>
                <div className="mt-3">
                    <div className="flex justify-between items-center text-[11px] mb-1.5 font-medium">
                        <span className="text-emerald-600 dark:text-emerald-400 font-semibold inline-flex items-center gap-1"><FaCircleCheck className="text-[10px]" /> {cuotasPagadas} pagadas</span>
                        <span className="text-rose-600 dark:text-rose-400 font-semibold inline-flex items-center gap-1"><FaCircleExclamation className="text-[10px]" /> {cuotasImpagas} impagas</span>
                        <span className="text-slate-500 dark:text-slate-400">{cuotasPendientes} restantes</span>
                    </div>
                    <div className="w-full flex h-2 rounded-full overflow-hidden bg-slate-200 dark:bg-slate-800">
                        <div className="bg-emerald-500" style={{ width: pct(cuotasPagadas) }} title={`${cuotasPagadas} cuotas pagadas`} />
                        <div className="bg-rose-500" style={{ width: pct(cuotasImpagas) }} title={`${cuotasImpagas} cuotas en mora`} />
                        <div className="bg-slate-300 dark:bg-slate-700" style={{ width: pct(cuotasPendientes) }} title={`${cuotasPendientes} cuotas pendientes`} />
                    </div>
                </div>
            </article>
        </section>
    );
};
